import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import { Icon } from '../components/ui/Icon';
import api from '../utils/api';

const fmtDate = (d) => {
  try {
    return new Date(d).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
  } catch { return ''; }
};

// Deterministic soft-pink tint per card when a post has no cover image.
const TINTS = [
  'from-pink-100 to-rose-50',
  'from-fuchsia-100 to-pink-50',
  'from-rose-100 to-pink-50',
  'from-pink-50 to-fuchsia-100',
];

function CategoryChip({ children }) {
  if (!children) return null;
  return (
    <span className="inline-block rounded-full bg-white/85 backdrop-blur text-tea-700 text-[10px] font-bold uppercase tracking-[0.15em] px-3 py-1 ring-1 ring-tea-100">
      {children}
    </span>
  );
}

function PostCard({ post, idx }) {
  const tint = TINTS[idx % TINTS.length];
  return (
    <Link
      to={`/press/${post.slug}`}
      className="group relative flex flex-col rounded-3xl bg-white ring-1 ring-gray-100 overflow-hidden shadow-[0_10px_40px_-24px_rgba(219,39,119,0.4)] hover:shadow-[0_24px_60px_-24px_rgba(219,39,119,0.55)] hover:-translate-y-1 transition-all duration-300"
    >
      <div className="relative aspect-[16/10] overflow-hidden">
        {post.coverImage ? (
          <img src={post.coverImage} alt={post.coverImageAlt || post.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
        ) : (
          <div className={'w-full h-full bg-gradient-to-br ' + tint + ' polka-pink-dense grid place-items-center'}>
            <span className="font-display text-2xl text-tea-300">AB8 Press</span>
          </div>
        )}
        <div className="absolute top-3 left-3">
          <CategoryChip>{post.category}</CategoryChip>
        </div>
      </div>
      <div className="flex flex-col flex-1 p-5">
        <h3 className="font-display text-xl font-bold text-gray-900 leading-snug group-hover:text-tea-700 transition-colors">
          {post.title}
        </h3>
        {post.excerpt && <p className="mt-2 text-sm text-gray-500 leading-relaxed line-clamp-3">{post.excerpt}</p>}
        <div className="mt-4 pt-3 border-t border-dashed border-tea-100 flex items-center justify-between text-[11px] text-gray-400">
          <span>{post.publishedAt ? fmtDate(post.publishedAt) : fmtDate(post.createdAt)}</span>
          <span className="inline-flex items-center gap-1.5">
            <span className="w-1.5 h-1.5 rounded-full bg-tea-300" />
            {post.readMinutes || 1} min read
          </span>
        </div>
      </div>
    </Link>
  );
}

export default function PressPage() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTag, setActiveTag] = useState(null);

  useEffect(() => {
    let active = true;
    api.get('/blog')
      .then(res => { if (active) setPosts(res.data || []); })
      .catch(() => {})
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, []);

  const tags = useMemo(() => {
    const s = new Set();
    posts.forEach(p => (p.tags || []).forEach(t => s.add(t)));
    return [...s].slice(0, 12);
  }, [posts]);

  const visible = useMemo(
    () => (activeTag ? posts.filter(p => (p.tags || []).includes(activeTag)) : posts),
    [posts, activeTag]
  );

  const featured = visible[0];
  const rest = visible.slice(1);

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Navbar />

      {/* ── Hero: pink gradient with white polka dots ───────────────────────── */}
      <section className="relative overflow-hidden text-white"
        style={{ background: 'linear-gradient(135deg, #f9a8d0 0%, #ec4899 45%, #be185d 100%)' }}>
        <div className="absolute inset-0 polka-white opacity-70" aria-hidden="true" />
        <div className="absolute -right-16 -top-20 h-64 w-64 rounded-full bg-white/15 blur-2xl" aria-hidden="true" />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28 text-center">
          <p className="text-white/80 text-xs font-bold uppercase tracking-[0.3em] mb-4">The AB8 Press</p>
          <h1 className="font-display text-5xl md:text-7xl font-extrabold tracking-tight leading-[1.05]">
            Glow Stories &amp; Beauty Edit
          </h1>
          <p className="mt-5 text-white/90 text-lg max-w-2xl mx-auto leading-relaxed">
            Rituals, ingredient deep-dives, and the latest from the AB8 Beauty atelier —
            the blog of 2026, served with a little sparkle.{' '}
            <Icon name="sparkles" className="inline w-4 h-4 align-[-2px]" />
          </p>
        </div>
        {/* scalloped dot strip at the bottom edge */}
        <div className="relative flex justify-center gap-2 pb-5" aria-hidden="true">
          {Array.from({ length: 9 }).map((_, i) => (
            <span key={i} className="w-2 h-2 rounded-full bg-white/70" />
          ))}
        </div>
      </section>

      <main className="flex-1 max-w-6xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-12 md:py-16">
        {/* Tag filter */}
        {tags.length > 0 && (
          <div className="flex flex-wrap items-center justify-center gap-2 mb-10">
            <button onClick={() => setActiveTag(null)}
              className={'text-xs font-semibold rounded-full px-4 py-2 border transition-colors ' +
                (!activeTag ? 'bg-tea-600 text-white border-tea-600' : 'bg-white text-gray-500 border-gray-200 hover:border-tea-300')}>
              All Stories
            </button>
            {tags.map(t => (
              <button key={t} onClick={() => setActiveTag(t === activeTag ? null : t)}
                className={'text-xs font-semibold rounded-full px-4 py-2 border transition-colors ' +
                  (activeTag === t ? 'bg-tea-600 text-white border-tea-600' : 'bg-white text-gray-500 border-gray-200 hover:border-tea-300')}>
                #{t}
              </button>
            ))}
          </div>
        )}

        {loading && <div className="py-24 text-center text-gray-400">Loading the latest stories…</div>}

        {!loading && visible.length === 0 && (
          <div className="py-24 text-center">
            <Icon name="mail" className="w-12 h-12 mx-auto mb-4 text-tea-400" />
            <p className="font-display text-2xl text-gray-700">The press desk is warming up.</p>
            <p className="text-gray-400 mt-2">Our first glow stories are on the way — check back soon.</p>
          </div>
        )}

        {/* Featured */}
        {!loading && featured && (
          <Link to={`/press/${featured.slug}`}
            className="group relative grid md:grid-cols-2 gap-0 rounded-[2rem] overflow-hidden ring-1 ring-tea-100 bg-white shadow-[0_20px_60px_-30px_rgba(219,39,119,0.5)] mb-14">
            <div className="relative aspect-[16/11] md:aspect-auto overflow-hidden">
              {featured.coverImage ? (
                <img src={featured.coverImage} alt={featured.coverImageAlt || featured.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              ) : (
                <div className="w-full h-full min-h-[16rem] bg-gradient-to-br from-pink-100 to-fuchsia-100 polka-pink-dense grid place-items-center">
                  <span className="font-display text-3xl text-tea-300">AB8 Press</span>
                </div>
              )}
            </div>
            <div className="relative p-8 md:p-10 flex flex-col justify-center polka-pink">
              <div className="flex items-center gap-2 mb-4">
                <span className="inline-block rounded-full bg-tea-600 text-white text-[10px] font-bold uppercase tracking-[0.18em] px-3 py-1">
                  ✦ Featured
                </span>
                <CategoryChip>{featured.category}</CategoryChip>
              </div>
              <h2 className="font-display text-3xl md:text-4xl font-extrabold text-gray-900 leading-tight group-hover:text-tea-700 transition-colors">
                {featured.title}
              </h2>
              {featured.excerpt && <p className="mt-4 text-gray-600 leading-relaxed">{featured.excerpt}</p>}
              <div className="mt-6 flex items-center gap-3 text-xs text-gray-400">
                {featured.author && <span className="font-medium text-gray-600">{featured.author}</span>}
                <span>·</span>
                <span>{featured.publishedAt ? fmtDate(featured.publishedAt) : fmtDate(featured.createdAt)}</span>
                <span>·</span>
                <span>{featured.readMinutes || 1} min read</span>
              </div>
              <span className="mt-6 inline-flex items-center gap-2 text-tea-700 font-semibold text-sm">
                Read the story
                <svg className="w-4 h-4 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
              </span>
            </div>
          </Link>
        )}

        {/* Grid */}
        {!loading && rest.length > 0 && (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-7">
            {rest.map((p, i) => <PostCard key={p.id} post={p} idx={i} />)}
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
}
